import { Component, OnInit, ViewChild, ElementRef, Input } from '@angular/core';
import * as d3 from 'd3';
import { MultipleDonutGraphConfigurations } from "./multiple-donut.configuration";
import { KlaHttpService } from "../../tools/services/kla.http.service";
import { CommonModule } from '@angular/common';

@Component({
  selector: 'kla-multiple-donut',
  templateUrl: './multiple-donut.component.html',
  styleUrls: ['./multiple-donut.component.scss'],
  standalone: true,
  imports: [ CommonModule ]
})
export class MultipleDonutComponent implements OnInit {
  @Input()
  public config: MultipleDonutGraphConfigurations;

  @Input()
  public chartAltText: string;

  @ViewChild('multipleDonut', {static: true}) private chartContainer: ElementRef;

  public imagePath: string;

  public selectedYear: number= 0;

  constructor(private httpService: KlaHttpService) { }

  ngOnInit() {
    if(this.config.data){
      this.imagePath = this.httpService.getImagePath(this.config.image, 500)
      this.createChart();
    }
  }

  @Input('width')
  set setWidth(width: number) {
    if (!this.config) { return; }
    this.config.width = width;
    this.ngOnInit();
  }

  createViewPort(width: number, height: number, translateScale: number) {
    d3.select(this.chartContainer.nativeElement).selectAll(".multiple-donut-graph").remove();
    return d3.select(this.chartContainer.nativeElement)
      .append('svg')
      .attr('class', 'pie multiple-donut-graph')
      .attr('width', width)
      .attr('height', height)
      .attr("viewBox", "0 0 408 408")
      .append('g')
      .attr('transform', 'translate(' + (10 + (width / translateScale)) + ',' + (10 + (height / 2)) + ')')
  }

  createChart() {

    let chartConfig = {
      width: 388,
      height: 368,
      thickness: 70,
      image: "",
      data: this.config.data
    }

    let whiteCircle = [ 
      { color: "#ffffff", value: 100 } 
    ]

    let width = this.config.width;
    let height = this.config.height;
    let thickness = this.config.thickness;

    let radius = Math.min(chartConfig.width, chartConfig.height) / 2;

    let pie = d3.pie<any>()
      .value(function (d) { return d.value; })
      .sort(null);

    let traslateScale = (width < 500 && width > 400) ? 4 : (width < 400) ? 2.2 : 3;

    if(traslateScale < 3) radius = Math.min( width - 40, height) / 2;

    let view = this.createViewPort(width, height, traslateScale);

    chartConfig.data[this.selectedYear].yearData.forEach((item, i) => {
      let shadow: boolean = (i > 0) ? true : false;

      if(shadow){
        let borderCircle = view.append("g");
        this.createOuterDonut(borderCircle, pie(whiteCircle), radius - thickness * i, radius - 2 - thickness * i, shadow);
      } 

      let dataItem = view.append("g");
      this.createOuterDonut(dataItem, pie(item), radius - thickness * i, thickness, false);

      this.addLabels(view, radius - thickness * i, thickness, radius, item[0].labelColor, item[0].labelText);
    })

    let imageThicknessOffset = (chartConfig.data[this.selectedYear].yearData.length == 3)? 1 : 0;
    let roundImageThicknessOffset = (chartConfig.data[this.selectedYear].yearData.length == 3)? 0 : 1;

    this.addImage(view, radius, thickness * (chartConfig.data.length + imageThicknessOffset ));

    (radius - thickness * chartConfig.data.length < thickness) ?  
          this.roundImage(view, pie(whiteCircle), radius - thickness * (chartConfig.data.length- roundImageThicknessOffset), (roundImageThicknessOffset == 1)? thickness: 20) :
          this.roundImage(view, pie(whiteCircle), radius - thickness * (chartConfig.data.length - roundImageThicknessOffset), thickness);
  }

  createOuterDonut(view, data, radius, thickness, addShadow) {
    let arc = d3.arc<any>()
      .innerRadius(radius - thickness)
      .outerRadius(radius);

    let path = view.selectAll('path')
      .data(data)
      .enter()
      .append("g")
      .attr("class", "parent-donut")
      .append('path')
      .attr('d', arc)
      .attr('fill', (d, i) => d.data.color)
      .style("filter", () => (addShadow) ? this.createDonutFilter(view) : "");
  }

  addLabels(view, radius, thickness, width, color, text) {
    view.append("line")
      .style("stroke", color)
      .attr("x1", 20)
      .attr("y1", -radius + thickness / 2)
      .attr("x2", width)
      .attr("y2", -radius + thickness / 2)
      .attr("stroke-width", 1.5);

    view.append("circle")
      .style("stroke", "gray")
      .style("fill", color)
      .attr("r", 5)
      .attr("cx", width)
      .attr("cy", -radius + thickness / 2);

    view.append("text")
      .attr("x", width + 10)
      .attr("y", -radius + thickness / 2 + 4)
      .text(text)
      .attr("font-family", "sans-serif")
      .attr("font-size", "12px")
      .attr("fill", "gray")
      .call(this.wrapLabel, 70, 12)

  }

  wrapLabel(text, width, lineHeight) {
    text.each(function (t, index) {
        var text = d3.select(this),
          words =  text.text().split(/\s+/).reverse(),
          word,
          line = [],
          lineNumber = 0,
          x = text.attr("x"),
          y = text.attr("y"),
          dy = parseFloat(text.attr("dy")),
          tspan = text.text(null).append("tspan");

        while (word = words.pop()) {
          line.push(word);
          tspan.text(line.join(" "));
          if (tspan.node()["getComputedTextLength"]() > width) {
            line.pop();
            tspan.text(line.join(" "));
            line = [word];
            tspan = text.append("tspan").attr("x", x).attr("dy",  lineHeight + "px").text(word);
          }
        }
    });
  }


  addImage(view, radius, thickness) {
    let imageWidth = (radius - thickness) * 2;
    let imageY = -radius + thickness;
    let imageX = -radius + thickness;

    // image attributes on mobile devices
    if(radius < thickness && imageWidth > -30) { 
       imageWidth = 50;
       imageX= -25;
       imageY = -25;
    }
    else if(imageWidth < -30){
      imageWidth = 20;
       imageX= -10;
       imageY = -10;
    }
     
    view.append("image")
      .attr("id", "inner-image")
      .attr("xlink:href", this.imagePath)
      .attr("x", imageX)
      .attr("y", imageY)
      .attr("width", imageWidth)
      .attr("height", imageWidth);
  }

  roundImage(view, data, radius, thickness) {
    let coverImage = view.append("g");
    (radius < 25)? this.createOuterDonut(coverImage, data, radius, 10, false):  this.createOuterDonut(coverImage, data, radius, thickness, false);
  }

  createDonutFilter(view) {
    let defs = view.append("svg:defs");

    let filter = defs.append("svg:filter")
      .attr("id", "drop-shadow")
      .attr("height", "130%");

    filter.append("feGaussianBlur")
      .attr("in", "SourceAlpha")
      .attr("stdDeviation", 5)
      .attr("result", "blur");

    filter.append("feOffset")
      .attr("in", "blur")
      .attr("dx", 0)
      .attr("dy", 0)
      .attr("result", "offsetBlur");

    let feMerge = filter.append("feMerge");

    feMerge.append("feMergeNode")
      .attr("in", "offsetBlur")
    feMerge.append("feMergeNode")
      .attr("in", "SourceGraphic");

    return "url(" + window.location.href + "#drop-shadow)";
  }

  changeYear(year:number) {
    this.selectedYear = this.config.data.findIndex(x=> x.year == year);
    this.ngOnInit();
  }
}
